import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {DraftsService, PatientDraft} from 'src/app/core/services/drafts.service';
import {Subscription} from 'rxjs';
import {PatientService} from '../../patients/services/patient.service';
import {JumpTextRendererComponent} from '@xmedical/report-builder';
import {PatientEvent, PatientEventsService} from '../../patients/services/patient-events.service';
import {DraftInteractionBehaviour} from 'src/app/core/layout/draft-overlay/draft-interaction-behaviour';
import {CommentsRepositoryService, Comment, Patient, Upload} from '@xmedical/core';
import {FileUploaderService, DownloadRequest} from '@xmedical/uploads';

@Component({
    selector: 'app-create-comment',
    templateUrl: './create-comment.component.html',
    styleUrls: ['./create-comment.component.scss'],
    standalone: false
})
export class CreateCommentComponent implements OnInit, OnDestroy, DraftInteractionBehaviour {

  @ViewChild('jumpTextRendererComponent') jumpTextRendererComponent: JumpTextRendererComponent;
  @Input() draft: PatientDraft;
  currentComment: Comment = null;
  loading = false;
  errorMessage = '';
  tags: string[] = ['patient', 'comment'];
  private commentsSubscription: Subscription[] = [];
  private debounceTimeout: NodeJS.Timeout;

  private draftService = inject(DraftsService);
  private commentsRepository = inject(CommentsRepositoryService);
  private patientService = inject(PatientService);
  private patientEventService = inject(PatientEventsService);
  private fileUploader = inject(FileUploaderService);

  get hasErrorMessage(): boolean {
    return this.errorMessage.length > 0;
  }

  get saveButtonEnabled(): boolean {
    return !this.loading;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  get hasComment(): boolean {
    return this.currentComment !== null;
  }

  get hasPatientDetails(): boolean {
    return this.patientService.hasPatientDetails;
  }

  ngOnInit(): void {
    if (this.draft.draftId !== 0) {
      this.loadCommentDetails();
    }
  }

  ngOnDestroy(): void {
    this.commentsSubscription.forEach(subscription => subscription.unsubscribe());
  }

  onTextReceived(text: string) {
    this.currentComment.comment.text += text;
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
        this.updateComment(false);
      },
      1500);
  }

  save(): void {
    this.updateComment(true);
  }

  onModelChange(text: string) {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
        this.updateComment(false);
      },
      1500);
  }

  onUploadComplete(result: boolean): void {
    if (result) {
      this.loadCommentDetails();
    }
  }

  onDownloadRequested(event: DownloadRequest): void {
    const upload = event.upload;
    const resolver = upload.legacy
      ? this.fileUploader.getSignedUrl(upload.path, 'GET', 300)
      : this.fileUploader.getSignedUploadsUrl(upload.path, 'GET', 300);

    resolver.subscribe({
      next: (url) => window.open(url, '_blank'),
      error: (err) => console.error('Failed to download file:', err)
    });
  }

  onDeleteRequested(upload: Upload): void {
    this.fileUploader.deleteUpload(upload.id, this.patient.id).subscribe({
      next: (success) => {
        if (success) {
          this.loadCommentDetails();
        }
      },
      error: (err) => console.error('Failed to delete file:', err)
    });
  }

  private loadCommentDetails() {
    this.loading = true;
    this.commentsSubscription.push(this.commentsRepository.get(this.draft.draftId).subscribe(comment => {
      this.currentComment = comment;
      this.loading = false;
    }, error => {
      this.errorMessage = 'Could not load comment';
      this.loading = false;
      console.error(error);
    }));
  }

  private updateComment(locked: boolean): void {
    this.currentComment.locked = locked;
    this.loading = locked;
    this.commentsSubscription.push(this.commentsRepository.update(this.currentComment).subscribe(() => {
      this.loading = false;
      if (locked) {
        this.draftService.closeDraft(this.draft.draftId, this.draft.draftType);
        this.patientEventService.emitEvent(new PatientEvent('comments_updated'));
      }
    }, error => {
      this.errorMessage = 'Could not save changes to this comment';
      this.loading = false;
      console.error(error);
    }));
  }
}
